/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the WYSIWYG paper.

   Real page space, real margins, real wrap — the SAME shared brains the
   export uses (`shared/zone-layout.ts` via paper/ZoneBand.tsx's ZoneBand, and
   `shared/body-layout.ts` via StudioBody). No "PAGE SPACE COLLAPSED"
   marker exists anywhere in this file; if the paper and the .docx ever
   disagree, that is a bug by definition (contract §3 R6).

   The header/footer/pre/post bands are the shared zone objects
   (ZoneShell + ZoneBandEdit) the project editor draws too — click to
   select, drag tokens onto them, the same snap targets. Only the body differs: the
   Studio is not editing a document, so the body is the realistic sample
   (StudioBody) instead of a TipTap surface.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import type { FormaSelection } from './useFormaSelection';
import { getUiScale, subscribeUiScale } from '../lib/uiScale';
import { EndOfSection, paperVars } from './paper/ZoneBand';
import { ZoneBandEdit } from './paper/ZoneBandEdit';
import { ZoneShell } from './paper/ZoneShell';
import { PageBreakElement, sampleBodyPagerSurface } from './paper/PageBreaks';
import { usePagination } from './paper/usePagination';
import { lastTextPage } from './paper/paginate';
import { resolvePageGeometry } from '../../shared/body-layout';
import { useTokenMarquee } from './useTokenMarquee';
import type { FormaEdit } from './panels/FormaPalettes';
import {
  bandIsEmpty,
  bandVariantName,
  positionalVariant,
  type BandKind,
  type PageVariant,
} from './formaZonesModel';
import type { DesignWiring } from './designWiring';
import { STUDIO_SAMPLE_SPEC_NAME, STUDIO_SAMPLE_SPEC_NUMBER } from './sampleSpec';
import { StudioBody } from './StudioBody';
import { peerSelectionCss } from './formaDraftModel';
import { Ruler } from './Ruler';
import { ZoneSpacer } from './ZoneSpacer';
import { SpacingBand } from './SpacingBand';
import { BlankPagePreview } from './BlankPagePreview';
import { eosDragMax, readZoneSpace, writeZoneSpace } from '../../shared/zone-space';

export interface StudioCanvasProps {
  forma: StandardConfig | null;
  zoom: number;
  design: DesignWiring;
  /** The ONE selection (useFormaSelection) — levels, tokens, EOS, band. */
  sel: FormaSelection;
  /** Click a sample paragraph: select that level (Explorer modifiers) AND
   *  open the Levels panel. */
  onLevelClick: (level: number, mods?: { ctrl?: boolean; shift?: boolean }) => void;
  /** Drives the ruler's drag handles and the whole-zone spacing bars —
   *  both are ordinary undoable forma edits (contract §2.7). */
  edit: FormaEdit;
  /** Where other people last touched the sheet: selection key -> cursor slot
   *  (Sync appendix item 8). Painted as a tinted outline from awareness. */
  peerSelections?: Record<string, number>;
  /** Ctrl+wheel zoom (Order 2, 2026-08-13) — one step per notch, through the
   *  SAME clamped setter the toolbar's +/− buttons use. */
  onZoomDelta?: (step: number) => void;
  /** Workspace ▸ View ▸ Pages: the sample breaks into true-size pages, so a
   *  forma edit shows its page effect. Off (default) = the one sheet. */
  paged?: boolean;
}

export function StudioCanvas({
  forma,
  zoom,
  design,
  sel,
  onLevelClick,
  edit,
  peerSelections,
  onZoomDelta,
  paged = false,
}: StudioCanvasProps) {
  const levelFocus = sel.levelFocus;
  // UI Scale dial (DESIGN.md §3.6): `zoom` is the RELATIVE percent (1.0 =
  // matches surroundings); the applied transform is relative × dial.
  const [dial, setDial] = React.useState(() => getUiScale().dial);
  React.useEffect(() => subscribeUiScale((v) => setDial(v.dial)), []);
  // ─── END OF SECTION selection ───
  // The EOS line is spaced like a level, so it is SELECTED like one: click
  // it and its space-before grows the same measured drag band a level's gap
  // grows (Task 3, 2026-08-22). One selection system (§3.4) — selecting a
  // level clears EOS, clicking empty canvas or Esc clears both, and it can
  // never be sticky. Since SYNC_PLAN §3.0 it is just the `eos` key in the
  // one selection (Ctrl toggles it alongside levels and tokens).
  const sheetBodyRef = React.useRef<HTMLDivElement | null>(null);
  const eosSel = sel.eos;

  // ─── Marquee select — the shared paper gesture (useTokenMarquee) ───
  // A plain click on empty canvas clears EVERYTHING — never sticky (§3.4):
  // tokens, the band, levels AND End of Section.
  const { beginMarquee, marqueeNode } = useTokenMarquee({ onEmptyClick: sel.clear });
  // Native non-passive listener — React's synthetic onWheel is passive, so
  // preventDefault can't stop the browser's own page-zoom there (ported
  // verbatim from the classic canvas's wheelZoomRef, app.tsx ~745).
  const wheelZoomRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !onZoomDelta) return;
      el.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          if (!e.ctrlKey) return;
          e.preventDefault();
          onZoomDelta(e.deltaY < 0 ? 0.05 : -0.05);
        },
        { passive: false }
      );
    },
    [onZoomDelta]
  );
  const paperContext = {
    forma,
    specNumber: STUDIO_SAMPLE_SPEC_NUMBER,
    specName: STUDIO_SAMPLE_SPEC_NAME,
    projectMeta: {},
    // A master is a template: field tokens draw as braced placeholders
    // ("{## ## ##}", "{Spec Name}") so nothing on this canvas looks like
    // literal text the export would print (Josh, 2026-08-14).
    placeholderPreview: true,
  };
  // ─── Pages view (each page wears its own band) ───
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const pagerSurface = React.useMemo(
    () => sampleBodyPagerSurface(() => sheetRef.current?.querySelector('.sfs-studio-body') ?? null),
    []
  );
  const { pages } = usePagination({ enabled: paged, forma, sheetRef, surface: pagerSurface });

  // Continuous: the sheet's header/footer show the variant being edited.
  // Pages: the sheet's header is page 1's, its footer the last text page's,
  // and every gesture on them edits THAT variant (ZoneShell `variant`).
  const geo = resolvePageGeometry(forma);
  const pageOf = (kind: BandKind) => (kind === 'footer' ? lastTextPage(pages) : 1);
  const shownVariant = (kind: BandKind): PageVariant =>
    paged ? positionalVariant(pageOf(kind), geo) : design.pageVariant;
  const bandProps = (kind: 'header' | 'footer') =>
    paged
      ? {
          variant: bandVariantName(shownVariant(kind)),
          pageNum: pageOf(kind),
          numPages: pages.numPages,
        }
      : { variant: bandVariantName(design.pageVariant) };

  const wrapBand = (band: BandKind, node: React.ReactNode) => (
    <ZoneShell
      band={band}
      design={design}
      variant={paged && (band === 'header' || band === 'footer') ? shownVariant(band) : undefined}
      empty={bandIsEmpty(forma, band, shownVariant(band))}
    >
      {node}
    </ZoneShell>
  );

  // Post-body zones are TOGGLABLE (Section End palette's "Enable post-body
  // zones" switch) — same field the classic canvas gated on
  // (`special.postBodyEnabled`) and the SAME field word-engine.ts reads at
  // export (`postGate`, line ~1959). Undefined falls back to "has content"
  // so nothing silently disappears; the explicit toggle always wins. OFF
  // must remove the band from the canvas entirely — not just empty its
  // content — exactly like the classic canvas's `{postOn && (...)}`.
  const sp = forma?.special;
  const hasPost = !!(sp?.postLeft?.length || sp?.postCenter?.length || sp?.postRight?.length);
  const postOn = sp?.postBodyEnabled ?? hasPost;
  // END OF SECTION spacing-before: drag handled by the SAME ZoneSpacer bar
  // every other band gets (header/pre/post/footer) — restores the vertical
  // drag the canvas lost, writing the SAME zoneSpace.eos.before field the
  // numeric "Spacing" control in the Section End palette writes (EosPanel).
  // EndOfSection gets ownSpacing=false so the gap is drawn once, by the
  // spacer, not doubled by the line's own marginTop.
  const eosOn = sp?.eosEnabled !== false;

  // ─── Pages view: the breaks between pages (live bands, see PageBreaks) ───
  const pageBreaks = React.useMemo(() => {
    if (!paged) return undefined;
    const out: Record<number, React.ReactNode> = {};
    pages.breaks.forEach((b) => {
      out[b.beforeBlockIndex] = (
        <PageBreakElement
          paper={paperContext}
          endsPage={b.pageNum - 1}
          numPages={pages.numPages}
          design={design}
        />
      );
    });
    return out;
    // paperContext is rebuilt every render from `forma`; key on forma.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged, pages, forma, design]);

  const sheetClasses = [
    'dsg-sheet',
    paged ? 'dsg-sheet--paged' : '',
    'dsg-zones-live',
    levelFocus ? `dsg-sel-l${levelFocus}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const peerCss = React.useMemo(
    () => (peerSelections ? peerSelectionCss(peerSelections) : ''),
    [peerSelections]
  );

  if (!forma) return null;

  return (
    <div
      className="dsg-canvas sfs-studio-canvas"
      style={{ ...paperVars(forma) }}
      ref={wheelZoomRef}
      onMouseDown={beginMarquee}
    >
      {peerCss && <style>{peerCss}</style>}
      <div className="sfs-canvas-scale" style={{ transform: `scale(${zoom * dial})` }}>
        <Ruler forma={forma} levelFocus={levelFocus} edit={edit} />

        <div className={sheetClasses} ref={sheetRef}>
          <div className="dsg-sheet-header">
            {wrapBand(
              'header',
              <ZoneBandEdit
                kind="header"
                {...bandProps('header')}
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                onZoneInsertAt={design.onZoneInsertAt}
                onZoneReplaceAt={design.onZoneReplaceAt}
                onNotice={design.onNotice}
                {...paperContext}
              />
            )}
            <ZoneSpacer forma={forma} region="header" edge="after" onEdit={edit} />
          </div>

          <div className={`dsg-sheet-body${eosSel ? ' sfs-eos-selected' : ''}`} ref={sheetBodyRef}>
            <ZoneSpacer forma={forma} region="pre" edge="before" onEdit={edit} />
            {wrapBand(
              'pre',
              <ZoneBandEdit
                kind="pre"
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                onZoneInsertAt={design.onZoneInsertAt}
                onZoneReplaceAt={design.onZoneReplaceAt}
                onNotice={design.onNotice}
                {...paperContext}
              />
            )}
            <ZoneSpacer forma={forma} region="pre" edge="after" onEdit={edit} />
            <StudioBody
              forma={forma}
              levelFocus={levelFocus}
              selectedLevels={sel.levels}
              onLevelClick={onLevelClick}
              edit={edit}
              pageBreaks={pageBreaks}
            />
            {postOn && (
              <>
                <ZoneSpacer forma={forma} region="post" edge="before" onEdit={edit} />
                {wrapBand(
                  'post',
                  <ZoneBandEdit
                    kind="post"
                    edit={edit}
                    onRequestTextEdit={design.onRequestTextEdit}
                    onZoneInsertAt={design.onZoneInsertAt}
                    onZoneReplaceAt={design.onZoneReplaceAt}
                    onNotice={design.onNotice}
                    {...paperContext}
                  />
                )}
                <ZoneSpacer forma={forma} region="post" edge="after" onEdit={edit} />
              </>
            )}
            {eosOn && <ZoneSpacer forma={forma} region="eos" edge="before" onEdit={edit} />}
            <EndOfSection
              {...paperContext}
              ownSpacing={false}
              onConfigure={(mods) => {
                sel.selectEos(mods);
                design.onOpenPanel?.('eos');
              }}
            />
            {eosOn && eosSel && (
              <SpacingBand
                hostRef={sheetBodyRef}
                /* The gap IS the eos spacer's box: its top edge is the bottom
                   of whatever precedes it, its bottom edge is the END OF
                   SECTION line (which carries ownSpacing={false}, so the gap
                   is drawn once, here). */
                getAbove={() =>
                  sheetBodyRef.current?.querySelector('[data-zone-space="eos-before"]')
                    ?.previousElementSibling ?? null
                }
                getBelow={() => sheetBodyRef.current?.querySelector('.dsg-eos') ?? null}
                valuePt={readZoneSpace(forma, 'eos', 'before')}
                label="End of section — space before"
                ariaLabel="End of section space before"
                min={0}
                max={eosDragMax(readZoneSpace(forma, 'eos', 'before'))}
                onCommit={(v) =>
                  edit((f) =>
                    writeZoneSpace(
                      f,
                      'eos',
                      'before',
                      v,
                      eosDragMax(readZoneSpace(f, 'eos', 'before'))
                    )
                  )
                }
              />
            )}
          </div>

          <div className="dsg-sheet-footer">
            <ZoneSpacer forma={forma} region="footer" edge="before" onEdit={edit} />
            {wrapBand(
              'footer',
              <ZoneBandEdit
                kind="footer"
                {...bandProps('footer')}
                edit={edit}
                onRequestTextEdit={design.onRequestTextEdit}
                onZoneInsertAt={design.onZoneInsertAt}
                onZoneReplaceAt={design.onZoneReplaceAt}
                onNotice={design.onNotice}
                {...paperContext}
              />
            )}
          </div>
        </div>

        {forma.special?.blankPageEnabled && (
          <div className="sfs-blankpage-wrap">
            <BlankPagePreview forma={forma} />
          </div>
        )}
      </div>

      {marqueeNode}
    </div>
  );
}
